import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ICondition,
  IEffect,
  IField,
  IInteraction,
  IRule,
  IValidation,
  IValidationError,
} from '../interfaces/MetadataInterfaces.js';
import { validate } from '../validationHelper.js';

export type RepeatingPanelAction =
  | 'first'
  | 'prev'
  | 'next'
  | 'last'
  | 'add'
  | 'remove';

export type HandleRepeatingPanelActionFn = (
  panelName: string,
  action: RepeatingPanelAction,
  removeAtIndex?: number
) => void;

export interface OtherInfoLookupValue {
  key: string;
  value: string;
  icon?: string;
}

export interface OtherInfoLookup {
  name: string;
  values: OtherInfoLookupValue[];
}

export interface OtherInfoField {
  name: string;
  fieldType?: string;
  label?: string;
  hint?: string;
  readonly?: boolean;
  readonlyForContractors?: boolean;
  lookupList?: string;
  multiSelect?: boolean | null;
  reference?: string;
  mandatory?: boolean;
  parentName?: string | null;
  dependees?: string;
  showInGrid?: boolean;
  labelAbove?: boolean;
  optionsLayout?: string;
  permissions?: string[];
  options?: any;
  rows?: number;
  isCollapsed?: boolean;
  effect?: string; // "Show" | "Hide"
  repeatingMode?: string | null;
  [key: string]: any;
}

export interface OtherInfoMetadata {
  lookups?: OtherInfoLookup[];
  fields: OtherInfoField[];
  validations?: IValidation[];
  tabs?: any[];
  interactions?: IInteraction[];
}

const TAB_EFFECT_PREFIX = 'tab:';

const comparableText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
};

const selectionTokens = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const tokens = selectionTokens(item);
      if (tokens) return tokens;
      const text = comparableText(item);
      return text ? [text] : [];
    });
  }

  const text = comparableText(value);
  if (!text?.includes('|')) return undefined;
  return text
    .split('|')
    .map((token) => token.trim())
    .filter(Boolean);
};

const selectionContains = (
  entityValue: unknown,
  ruleValue: unknown
): boolean => {
  const rule = comparableText(ruleValue);
  if (!rule) return false;

  const tokens = selectionTokens(entityValue);
  if (tokens) return tokens.some((token) => token === rule);

  const text = comparableText(entityValue);
  return text != null && text.includes(rule);
};

const normalizeComparableValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const lowered = trimmed.toLowerCase();

    if (lowered === 'true') return true;
    if (lowered === 'false') return false;

    const numeric = Number(trimmed);
    if (trimmed !== '' && !Number.isNaN(numeric)) {
      return numeric;
    }

    return trimmed;
  }

  return value;
};

export const matchesOtherInfoCondition = (
  entity: Record<string, any>,
  query: ICondition
): boolean => {
  if (!query.rules || query.rules.length === 0) {
    return true;
  }

  const results = query.rules.map((rule: ICondition | IRule) => {
    if ('combinator' in rule) {
      return matchesOtherInfoCondition(entity, rule);
    }

    const { field, operator, value } = rule;
    const entityValue = normalizeComparableValue(entity[field]);
    const ruleValue = normalizeComparableValue(value);

    switch (operator) {
      case '=':
        return entityValue === ruleValue;
      case '!=':
        return entityValue !== ruleValue;
      case 'contains':
        return selectionContains(entity[field], value);
      case 'doesNotContain':
        return !selectionContains(entity[field], value);
      case '<':
        return (entityValue as any) < (ruleValue as any);
      case '>':
        return (entityValue as any) > (ruleValue as any);
      default:
        return false;
    }
  });

  return query.combinator?.toLowerCase() === 'and'
    ? results.every(Boolean)
    : results.some(Boolean);
};

export interface UseOtherInfoParams {
  metadata: OtherInfoMetadata;
  otherInfo: any;
  isSaved: React.MutableRefObject<boolean>;
  validationErrors?: IValidationError[];
  onOtherInfoChanged: (newValue: any) => void;
  onValidationErrorsChanged?: (newErrors: IValidationError[]) => unknown;
  relatedEntities?: { id: number; entityInfo: string }[];
  /**
   * True when the current session is a contractor. Callers on platforms
   * without token/role access (e.g. the dedicated contractor mobile app)
   * can just pass `true` since that app is contractor-only.
   */
  isContractorRole?: boolean;
}

export interface OtherInfoCompletion {
  completedFields: number;
  totalFields: number;
  percentage: number;
}

export type CompletionStatusFlag = 'complete' | 'incomplete' | 'not-applicable';

export interface CompletionContextOverride {
  panelName?: string;
  panelIndex?: number;
  entityId?: number;
}

export interface PanelFieldWriteContext {
  entityId?: number;
  manualIndex?: number;
}

export interface OtherInfoFieldCompletionIndicator {
  fieldName: string;
  status: CompletionStatusFlag;
  isCompletable: boolean;
  isComplete: boolean;
}

export interface OtherInfoPanelCompletionIndicator {
  panelName: string;
  status: CompletionStatusFlag;
  isComplete: boolean;
  completedFields: number;
  totalFields: number;
  percentage: number;
}

const NON_COMPLETABLE_FIELD_TYPES = new Set([
  'Panel',
  'Text',
  'Link',
  'FileViewer',
]);

const isValueCompleted = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  if (typeof value === 'boolean') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((entry) => isValueCompleted(entry));
  }

  if (typeof value === 'object') {
    const objectEntries = Object.entries(
      value as Record<string, unknown>
    ).filter(([key]) => key !== 'id' && key !== '_panelName');

    if (objectEntries.length === 0) {
      return false;
    }

    return objectEntries.some(([, entryValue]) => isValueCompleted(entryValue));
  }

  return false;
};

const isFieldCompletable = (
  field: OtherInfoField,
  isContractorRole: boolean
): boolean => {
  if (!field?.name) {
    return false;
  }

  if (
    field.readonly ||
    (field.readonlyForContractors && isContractorRole) ||
    field.effect === 'Hide' ||
    field.effect === 'Readonly'
  ) {
    return false;
  }

  if (isContractorRole && field.fieldType === 'Checkbox') {
    return false;
  }

  if (field.fieldType && NON_COMPLETABLE_FIELD_TYPES.has(field.fieldType)) {
    return false;
  }

  return true;
};

const resolveFieldValueForCompletion = (
  fieldName: string,
  parsedOtherInfo: Record<string, any>,
  metadataFields: OtherInfoField[],
  repeatingPanelIndices: Record<string, number>,
  currentEntityInRepeatingPanel: number,
  contextOverride?: CompletionContextOverride
): { value: unknown; hasActiveContext: boolean } => {
  const { fieldParentName, manualRepeating, entityRepeating } =
    getFieldPanelContext(fieldName, metadataFields);

  if (fieldParentName && manualRepeating) {
    const panelEntries = parsedOtherInfo[fieldParentName];
    if (!Array.isArray(panelEntries) || panelEntries.length === 0) {
      return { value: undefined, hasActiveContext: false };
    }

    let panelIndex = repeatingPanelIndices[fieldParentName] ?? 0;
    if (
      contextOverride?.panelName === fieldParentName &&
      typeof contextOverride.panelIndex === 'number'
    ) {
      panelIndex = contextOverride.panelIndex;
    }

    const safeIndex = Math.min(
      Math.max(panelIndex, 0),
      panelEntries.length - 1
    );

    return {
      value: panelEntries[safeIndex]?.[fieldName],
      hasActiveContext: true,
    };
  }

  if (fieldParentName && entityRepeating) {
    const panelEntries = parsedOtherInfo[fieldParentName];
    if (!Array.isArray(panelEntries) || panelEntries.length === 0) {
      return { value: undefined, hasActiveContext: false };
    }

    let entityId = currentEntityInRepeatingPanel;
    if (
      contextOverride?.panelName === fieldParentName &&
      typeof contextOverride.entityId === 'number'
    ) {
      entityId = contextOverride.entityId;
    }

    if (!entityId) {
      return { value: undefined, hasActiveContext: false };
    }

    const entityIndex = panelEntries.findIndex(
      (entity: any) => entity.id === entityId
    );
    if (entityIndex < 0) {
      return { value: undefined, hasActiveContext: false };
    }

    return {
      value: panelEntries[entityIndex]?.[fieldName],
      hasActiveContext: true,
    };
  }

  return {
    value: parsedOtherInfo[fieldName],
    hasActiveContext: true,
  };
};

const isFieldDescendantOfPanel = (
  fieldName: string,
  panelName: string,
  metadataFields: OtherInfoField[]
): boolean => {
  const field = metadataFields.find((f) => f.name === fieldName);
  let currentParentName = field?.parentName ?? undefined;

  while (currentParentName) {
    if (currentParentName === panelName) {
      return true;
    }
    const parent = metadataFields.find((f) => f.name === currentParentName);
    currentParentName = parent?.parentName ?? undefined;
  }

  return false;
};

export const calculateOtherInfoCompletion = (
  parsedOtherInfo: Record<string, any>,
  metadataFields: OtherInfoField[],
  isContractorRole = false
): OtherInfoCompletion => {
  const completableFields = metadataFields.filter((field) =>
    isFieldCompletable(field, isContractorRole)
  );

  const totals = completableFields.reduce(
    (acc, field) => {
      const { fieldParentName, manualRepeating, entityRepeating } =
        getFieldPanelContext(field.name, metadataFields);

      if (fieldParentName && (manualRepeating || entityRepeating)) {
        const panelEntries = parsedOtherInfo[fieldParentName];
        if (!Array.isArray(panelEntries) || panelEntries.length === 0) {
          return acc;
        }

        acc.totalFields += panelEntries.length;
        panelEntries.forEach((entry: Record<string, any>) => {
          if (isValueCompleted(entry?.[field.name])) {
            acc.completedFields += 1;
          }
        });
        return acc;
      }

      acc.totalFields += 1;
      if (isValueCompleted(parsedOtherInfo[field.name])) {
        acc.completedFields += 1;
      }

      return acc;
    },
    { completedFields: 0, totalFields: 0 }
  );

  const percentage =
    totals.totalFields > 0
      ? Math.round((totals.completedFields / totals.totalFields) * 100)
      : 0;

  return {
    completedFields: totals.completedFields,
    totalFields: totals.totalFields,
    percentage,
  };
};

export const parseOtherInfo = (value?: any): Record<string, any> => {
  if (!value) return {};
  if (typeof value === 'object') return value ?? {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'string') {
        try {
          return JSON.parse(parsed);
        } catch {
          return {};
        }
      }
      return parsed ?? {};
    } catch {
      return {};
    }
  }
  return {};
};

/**
 * Resolves a field's panel context from metadata.
 * Pure function -- reusable anywhere panel awareness is needed.
 */
export const getFieldPanelContext = (
  fieldName: string,
  metadataFields: OtherInfoField[]
) => {
  const fieldDef = metadataFields.find((f) => f.name === fieldName);
  let currentParentName = fieldDef?.parentName ?? undefined;

  while (currentParentName) {
    const parentField = metadataFields.find(
      (f) => f.name === currentParentName
    );
    const mode = parentField?.repeatingMode;
    if (mode?.startsWith('M')) {
      return {
        fieldParentName: currentParentName,
        manualRepeating: true,
        entityRepeating: false,
      };
    }
    if (mode?.startsWith('E')) {
      return {
        fieldParentName: currentParentName,
        manualRepeating: false,
        entityRepeating: true,
      };
    }
    currentParentName = parentField?.parentName ?? undefined;
  }

  return {
    fieldParentName: fieldDef?.parentName ?? undefined,
    manualRepeating: false,
    entityRepeating: false,
  };
};

export const applyFieldValueToOtherInfo = (
  newOtherInfo: Record<string, any>,
  fieldName: string,
  value: string | object | null | undefined,
  metadataFields: OtherInfoField[],
  getRepeatingPanelIndex: (name: string) => number,
  currentElementInEntityRepeatingPanel: number,
  writeContext?: PanelFieldWriteContext
): boolean => {
  const { fieldParentName, manualRepeating, entityRepeating } =
    getFieldPanelContext(fieldName, metadataFields);

  const dependentFields = metadataFields.filter(
    (f) => f.dependees === fieldName
  );

  if (fieldParentName && (manualRepeating || entityRepeating)) {
    const panelEntries = newOtherInfo[fieldParentName];
    if (!Array.isArray(panelEntries) || panelEntries.length === 0) {
      return false;
    }

    let safeIndex: number;
    if (manualRepeating) {
      const rawIdx =
        writeContext?.manualIndex !== undefined
          ? writeContext.manualIndex
          : getRepeatingPanelIndex(fieldParentName);
      safeIndex = Math.min(Math.max(rawIdx, 0), panelEntries.length - 1);
    } else {
      const targetId =
        writeContext?.entityId !== undefined
          ? writeContext.entityId
          : currentElementInEntityRepeatingPanel;
      safeIndex = panelEntries.findIndex(
        (entity: any) =>
          entity && (entity.id === targetId || entity.id == targetId)
      );
    }

    if (safeIndex < 0) {
      return false;
    }

    const updatedEntry = { ...(panelEntries[safeIndex] ?? {}) };
    if (value === undefined || value === '' || value === null) {
      delete updatedEntry[fieldName];
    } else {
      updatedEntry[fieldName] = value;
    }
    for (const dep of dependentFields) {
      delete updatedEntry[dep.name];
    }
    const updatedEntries = [...panelEntries];
    updatedEntries[safeIndex] = updatedEntry;
    newOtherInfo[fieldParentName] = updatedEntries;
    return true;
  }

  if (value === undefined || value === '' || value === null) {
    delete newOtherInfo[fieldName];
  } else {
    newOtherInfo[fieldName] = value;
  }
  for (const dep of dependentFields) {
    delete newOtherInfo[dep.name];
  }
  return true;
};

type OtherInfoTab = { tabName: string; hidden?: boolean | undefined };

const initialEffectsByMetadata = new WeakMap<
  object,
  {
    fields: Map<string, string | undefined>;
    tabs: Map<string, boolean | undefined>;
  }
>();

const initialEffectsOf = (metadata: OtherInfoMetadata) => {
  const known = initialEffectsByMetadata.get(metadata);
  if (known) return known;
  const initial = {
    fields: new Map<string, string | undefined>(
      (metadata.fields ?? []).map((field) => [field.name, field.effect])
    ),
    tabs: new Map<string, boolean | undefined>(
      ((metadata.tabs ?? []) as OtherInfoTab[]).map((tab) => [
        tab.tabName,
        tab.hidden,
      ])
    ),
  };
  initialEffectsByMetadata.set(metadata, initial);
  return initial;
};

const applyEffect = (
  effect: IEffect,
  fields: OtherInfoField[],
  tabs: OtherInfoTab[]
) => {
  const hidden = effect.effectType === 'Hide';
  if (effect.effectType !== 'Show' && !hidden) return;
  effect.fields.forEach((fieldName: string) => {
    if (fieldName.startsWith(TAB_EFFECT_PREFIX)) {
      const tab = tabs.find(
        (candidate) =>
          candidate.tabName === fieldName.slice(TAB_EFFECT_PREFIX.length)
      );
      if (tab) tab.hidden = hidden;
    }
    const field = fields.find((candidate) => candidate.name === fieldName);
    if (field) field.effect = hidden ? 'Hide' : 'Show';
  });
};

export const applyInteractions = (
  metadata: OtherInfoMetadata,
  otherInfo: Record<string, any>
): string => {
  const initial = initialEffectsOf(metadata);
  const fields = metadata.fields ?? [];
  const tabs = (metadata.tabs ?? []) as OtherInfoTab[];

  fields.forEach((field) => {
    if (!initial.fields.has(field.name)) {
      initial.fields.set(field.name, field.effect);
    }
    const initialEffect = initial.fields.get(field.name);
    if (field.effect === initialEffect) return;
    if (initialEffect === undefined) delete field.effect;
    else field.effect = initialEffect;
  });
  tabs.forEach((tab) => {
    if (!initial.tabs.has(tab.tabName)) {
      initial.tabs.set(tab.tabName, tab.hidden);
    }
    const initialHidden = initial.tabs.get(tab.tabName);
    if (tab.hidden !== initialHidden) tab.hidden = initialHidden;
  });

  (metadata.interactions ?? []).forEach((interaction) => {
    if (
      matchesOtherInfoCondition(otherInfo, interaction.condition as ICondition)
    ) {
      interaction.effects.forEach((effect) =>
        applyEffect(effect as IEffect, fields, tabs)
      );
    }
  });

  return JSON.stringify([
    fields.map((field) => field.effect),
    tabs.map((tab) => tab.hidden),
  ]);
};

export const useOtherInfo = (params: UseOtherInfoParams) => {
  const [otherInfo, setOtherInfo] = useState(params.otherInfo);
  const otherInfoObj = useMemo(() => parseOtherInfo(otherInfo), [otherInfo]);
  const metadataFields = params.metadata.fields ?? [];
  const allOptionsObj = useRef<any>(null);
  const onOtherInfoChangedRef = useRef(params.onOtherInfoChanged);
  onOtherInfoChangedRef.current = params.onOtherInfoChanged;
  const lastNotifiedValue = useRef<string | null>(null);
  const [repeatingPanelIndices, setRepeatingPanelIndices] = useState<
    Record<string, number>
  >({});
  const [
    currentElementInEntityRepeatingPanel,
    setCurrentElementInEntityRepeatingPanel,
  ] = useState<number>(0);
  const effectsKey = useMemo(
    () => applyInteractions(params.metadata, otherInfoObj),
    [params.metadata, otherInfoObj]
  );

  const completion = useMemo(
    () =>
      calculateOtherInfoCompletion(
        otherInfoObj,
        metadataFields,
        params.isContractorRole
      ),
    [otherInfoObj, metadataFields, effectsKey, params.isContractorRole]
  );

  const getFieldCompletionIndicator = useCallback(
    (
      fieldName: string,
      contextOverride?: CompletionContextOverride
    ): OtherInfoFieldCompletionIndicator => {
      const field = metadataFields.find((f) => f.name === fieldName);
      if (
        !field ||
        !isFieldCompletable(field, Boolean(params.isContractorRole))
      ) {
        return {
          fieldName,
          status: 'not-applicable',
          isCompletable: false,
          isComplete: false,
        };
      }

      const { value, hasActiveContext } = resolveFieldValueForCompletion(
        fieldName,
        otherInfoObj,
        metadataFields,
        repeatingPanelIndices,
        currentElementInEntityRepeatingPanel,
        contextOverride
      );

      if (!hasActiveContext) {
        return {
          fieldName,
          status: 'not-applicable',
          isCompletable: true,
          isComplete: false,
        };
      }

      const isComplete = isValueCompleted(value);

      return {
        fieldName,
        status: isComplete ? 'complete' : 'incomplete',
        isCompletable: true,
        isComplete,
      };
    },
    [
      metadataFields,
      otherInfoObj,
      repeatingPanelIndices,
      currentElementInEntityRepeatingPanel,
      params.isContractorRole,
    ]
  );

  const getPanelCompletionIndicator = useCallback(
    (
      panelName: string,
      contextOverride?: CompletionContextOverride
    ): OtherInfoPanelCompletionIndicator => {
      const panelField = metadataFields.find(
        (f) => f.name === panelName && f.fieldType === 'Panel'
      );

      if (!panelField) {
        return {
          panelName,
          status: 'not-applicable',
          isComplete: false,
          completedFields: 0,
          totalFields: 0,
          percentage: 0,
        };
      }

      const fieldsInPanel = metadataFields.filter(
        (field) =>
          isFieldCompletable(field, Boolean(params.isContractorRole)) &&
          isFieldDescendantOfPanel(field.name, panelName, metadataFields)
      );

      let completedFields = 0;
      let totalFields = 0;

      fieldsInPanel.forEach((field) => {
        const fieldContextOverride: CompletionContextOverride = { panelName };
        if (typeof contextOverride?.panelIndex === 'number') {
          fieldContextOverride.panelIndex = contextOverride.panelIndex;
        }
        if (typeof contextOverride?.entityId === 'number') {
          fieldContextOverride.entityId = contextOverride.entityId;
        }

        const fieldIndicator = getFieldCompletionIndicator(
          field.name,
          fieldContextOverride
        );

        if (fieldIndicator.status === 'not-applicable') {
          return;
        }

        totalFields += 1;
        if (fieldIndicator.isComplete) {
          completedFields += 1;
        }
      });

      const percentage =
        totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
      const isComplete = totalFields > 0 && completedFields === totalFields;
      const status: CompletionStatusFlag =
        totalFields === 0
          ? 'not-applicable'
          : isComplete
          ? 'complete'
          : 'incomplete';

      return {
        panelName,
        status,
        isComplete,
        completedFields,
        totalFields,
        percentage,
      };
    },
    [metadataFields, getFieldCompletionIndicator, params.isContractorRole]
  );

  const fieldCompletionIndicators = useMemo(() => {
    const indicators: Record<string, OtherInfoFieldCompletionIndicator> = {};

    metadataFields.forEach((field) => {
      indicators[field.name] = getFieldCompletionIndicator(field.name);
    });

    return indicators;
  }, [metadataFields, getFieldCompletionIndicator]);

  const panelCompletionIndicators = useMemo(() => {
    const indicators: Record<string, OtherInfoPanelCompletionIndicator> = {};

    metadataFields
      .filter((field) => field.fieldType === 'Panel')
      .forEach((panelField) => {
        indicators[panelField.name] = getPanelCompletionIndicator(
          panelField.name
        );
      });

    return indicators;
  }, [metadataFields, getPanelCompletionIndicator]);

  const isFieldComplete = useCallback(
    (fieldName: string, contextOverride?: CompletionContextOverride): boolean =>
      getFieldCompletionIndicator(fieldName, contextOverride).isComplete,
    [getFieldCompletionIndicator]
  );

  const isPanelComplete = useCallback(
    (panelName: string, contextOverride?: CompletionContextOverride): boolean =>
      getPanelCompletionIndicator(panelName, contextOverride).isComplete,
    [getPanelCompletionIndicator]
  );

  const getRepeatingPanelIndex = useCallback(
    (panelName: string) => repeatingPanelIndices[panelName] ?? 0,
    [repeatingPanelIndices]
  );

  const setRepeatingPanelIndex = useCallback(
    (panelName: string, updater: number | ((prev: number) => number)) => {
      setRepeatingPanelIndices((prev) => {
        const currentIndex = prev[panelName] ?? 0;
        const newIndex =
          typeof updater === 'function' ? updater(currentIndex) : updater;
        return { ...prev, [panelName]: newIndex };
      });
    },
    []
  );

  allOptionsObj.current = useMemo(() => {
    const newMap = new Map<string, string>();
    params.metadata.fields.forEach((field) => {
      const lookup = params.metadata.lookups?.find(
        (l) => l.name === field.lookupList
      );
      if (lookup) {
        lookup.values.forEach((lookupValue) => {
          newMap.set(`${lookup.name}_${lookupValue.key}`, lookupValue.value);
        });
      }
    });
    return Object.fromEntries(newMap);
  }, [params.metadata]);

  const hasActiveRepeatingInstance = useCallback(
    (panelName?: string): boolean => {
      if (!panelName) return true;
      const panelEntries = otherInfoObj[panelName];
      const index = getRepeatingPanelIndex(panelName);
      return (
        Array.isArray(panelEntries) &&
        panelEntries.length > 0 &&
        index >= 0 &&
        index < panelEntries.length
      );
    },
    [getRepeatingPanelIndex, otherInfoObj]
  );

  const hasActiveEntityRepeatingInstance = useCallback(
    (panelName?: string): boolean => {
      if (!panelName) return true;
      return (
        params.relatedEntities !== undefined &&
        params.relatedEntities.length > 0 &&
        currentElementInEntityRepeatingPanel !== 0 &&
        params.relatedEntities.findIndex(
          (entity) => entity.id === currentElementInEntityRepeatingPanel
        ) >= 0
      );
    },
    [currentElementInEntityRepeatingPanel, params.relatedEntities]
  );

  const applyOtherInfoFieldChange = useCallback(
    (
      fieldName: string,
      value: string | object | null | undefined,
      writeContext?: PanelFieldWriteContext
    ) => {
      setOtherInfo((prev: any) => {
        const newOtherInfo = parseOtherInfo(prev);
        const ok = applyFieldValueToOtherInfo(
          newOtherInfo,
          fieldName,
          value,
          params.metadata.fields,
          getRepeatingPanelIndex,
          currentElementInEntityRepeatingPanel,
          writeContext
        );
        if (!ok) {
          return prev;
        }
        return JSON.stringify(newOtherInfo);
      });
    },
    [
      currentElementInEntityRepeatingPanel,
      getRepeatingPanelIndex,
      params.metadata.fields,
    ]
  );

  const handleOtherInfoBatchChange = useCallback(
    (
      updates: Record<string, string | object | null | undefined>,
      writeContext?: PanelFieldWriteContext
    ) => {
      const keys = Object.keys(updates);
      if (keys.length === 0) {
        return;
      }
      if (keys.length === 1) {
        const k = keys[0] as string;
        applyOtherInfoFieldChange(k, updates[k], writeContext);
        return;
      }
      setOtherInfo((prev: any) => {
        const parsed = parseOtherInfo(prev);
        const newOtherInfo = parseOtherInfo(JSON.stringify(parsed)) as Record<
          string,
          any
        >;
        for (const [fieldName, value] of Object.entries(updates)) {
          const ok = applyFieldValueToOtherInfo(
            newOtherInfo,
            fieldName,
            value,
            params.metadata.fields,
            getRepeatingPanelIndex,
            currentElementInEntityRepeatingPanel,
            writeContext
          );
          if (!ok) {
            return prev;
          }
        }
        return JSON.stringify(newOtherInfo);
      });
    },
    [
      applyOtherInfoFieldChange,
      currentElementInEntityRepeatingPanel,
      getRepeatingPanelIndex,
      params.metadata.fields,
    ]
  );

  const handleOtherInfoChange = useCallback(
    (fieldName: string, value?: string | object | null) => {
      applyOtherInfoFieldChange(fieldName, value, undefined);
    },
    [applyOtherInfoFieldChange]
  );

  const handleOtherInfoChangeWithEntity = useCallback(
    (entityId: number) =>
      (fieldName: string, value?: string | object | null) => {
        applyOtherInfoFieldChange(fieldName, value, { entityId });
      },
    [applyOtherInfoFieldChange]
  );

  const handleOtherInfoChangeWithManualIndex = useCallback(
    (manualIndex: number) =>
      (fieldName: string, value?: string | object | null) => {
        applyOtherInfoFieldChange(fieldName, value, { manualIndex });
      },
    [applyOtherInfoFieldChange]
  );

  useEffect(() => {
    const parsed = parseOtherInfo(otherInfo);
    const stringified = JSON.stringify(parsed);

    if (stringified !== lastNotifiedValue.current) {
      lastNotifiedValue.current = stringified;
      onOtherInfoChangedRef.current(parsed);
    }
  }, [otherInfo]);

  useEffect(() => {
    const incomingStringified = JSON.stringify(
      parseOtherInfo(params.otherInfo)
    );
    if (JSON.stringify(parseOtherInfo(otherInfo)) !== incomingStringified) {
      lastNotifiedValue.current = incomingStringified;
      setOtherInfo(incomingStringified);
    }
  }, [params.otherInfo]);

  const handleRepeatingPanelAction = useCallback<HandleRepeatingPanelActionFn>(
    (panelName, action, removeAtIndex) => {
      switch (action) {
        case 'first':
          setRepeatingPanelIndex(panelName, 0);
          break;
        case 'prev':
          setRepeatingPanelIndex(panelName, (prev) => Math.max(prev - 1, 0));
          break;
        case 'next': {
          const newOtherInfo = parseOtherInfo(otherInfo);
          const currentIndex = getRepeatingPanelIndex(panelName);
          if (
            !newOtherInfo[panelName] ||
            newOtherInfo[panelName].length === 0 ||
            newOtherInfo[panelName].length - 1 <= currentIndex
          ) {
            return;
          }
          setRepeatingPanelIndex(panelName, (prev) =>
            Math.min(prev + 1, newOtherInfo[panelName].length - 1)
          );
          break;
        }
        case 'last': {
          const newOtherInfo = parseOtherInfo(otherInfo);
          const panelEntries = Array.isArray(newOtherInfo[panelName])
            ? newOtherInfo[panelName]
            : [];
          if (panelEntries.length === 0) {
            setRepeatingPanelIndex(panelName, 0);
            return;
          }
          setRepeatingPanelIndex(panelName, panelEntries.length - 1);
          break;
        }
        case 'add': {
          setOtherInfo((prev: any) => {
            const newOtherInfo = parseOtherInfo(prev);
            const newElementsInRepeatingPanel = Array.isArray(
              newOtherInfo[panelName]
            )
              ? [...newOtherInfo[panelName]]
              : [];
            newElementsInRepeatingPanel.push({
              id: newElementsInRepeatingPanel.length + 1,
              _panelName: panelName,
            });
            newOtherInfo[panelName] = newElementsInRepeatingPanel;
            params.onOtherInfoChanged(newOtherInfo);
            setRepeatingPanelIndex(
              panelName,
              Math.max(newElementsInRepeatingPanel.length - 1, 0)
            );
            return JSON.stringify(newOtherInfo);
          });
          break;
        }
        case 'remove': {
          const preferredIndex =
            removeAtIndex !== undefined
              ? removeAtIndex
              : getRepeatingPanelIndex(panelName);
          setOtherInfo((prev: any) => {
            const newOtherInfo = parseOtherInfo(prev);
            const newElementsInRepeatingPanel = Array.isArray(
              newOtherInfo[panelName]
            )
              ? [...newOtherInfo[panelName]]
              : [];
            if (newElementsInRepeatingPanel.length === 0) {
              return prev;
            }
            const currentIndex = Math.max(
              0,
              Math.min(preferredIndex, newElementsInRepeatingPanel.length - 1)
            );
            newElementsInRepeatingPanel.splice(currentIndex, 1);
            newOtherInfo[panelName] = newElementsInRepeatingPanel;
            params.onOtherInfoChanged(newOtherInfo);
            setRepeatingPanelIndex(panelName, (prevIndex) =>
              Math.max(
                0,
                Math.min(prevIndex, newElementsInRepeatingPanel.length - 1)
              )
            );
            return JSON.stringify(newOtherInfo);
          });
          break;
        }
      }
    },
    [getRepeatingPanelIndex, setRepeatingPanelIndex, otherInfo, params]
  );

  const ensureEntityRepeatingEntry = useCallback(
    (panelName: string, entityId: number) => {
      const currentInfo = parseOtherInfo(otherInfo);
      const existingEntries = Array.isArray(currentInfo[panelName])
        ? currentInfo[panelName]
        : [];

      const exists =
        existingEntries.findIndex((e: any) => e.id === entityId) >= 0;

      if (!exists) {
        setOtherInfo((prev: any) => {
          const newOtherInfo = parseOtherInfo(prev);
          const newElementsInRepeatingPanel = Array.isArray(
            newOtherInfo[panelName]
          )
            ? [...newOtherInfo[panelName]]
            : [];
          newElementsInRepeatingPanel.push({
            id: entityId,
            _panelName: panelName,
          });
          newOtherInfo[panelName] = newElementsInRepeatingPanel;
          params.onOtherInfoChanged(newOtherInfo);
          return JSON.stringify(newOtherInfo);
        });
      }
      setCurrentElementInEntityRepeatingPanel(entityId);
    },
    [otherInfo, params]
  );

  const validateField = useCallback(
    (fieldName: string, value: any, writeContext?: PanelFieldWriteContext) => {
      const field = params.metadata.fields.find((f) => f.name === fieldName);
      if (!field) return;

      const validation = params.metadata.validations?.find(
        (v) => v.field === fieldName
      );
      if (!validation) return;

      const tempEntity = { ...otherInfoObj };
      const { fieldParentName, manualRepeating, entityRepeating } =
        getFieldPanelContext(fieldName, params.metadata.fields);

      if (fieldParentName && (manualRepeating || entityRepeating)) {
        const panelEntries = tempEntity[fieldParentName];
        if (Array.isArray(panelEntries) && panelEntries.length > 0) {
          let safeIndex: number;
          if (manualRepeating) {
            const rawIdx =
              writeContext?.manualIndex !== undefined
                ? writeContext.manualIndex
                : getRepeatingPanelIndex(fieldParentName);
            safeIndex = Math.min(Math.max(rawIdx, 0), panelEntries.length - 1);
          } else {
            const targetId =
              writeContext?.entityId !== undefined
                ? writeContext.entityId
                : currentElementInEntityRepeatingPanel;
            safeIndex = panelEntries.findIndex(
              (entity: any) =>
                entity && (entity.id === targetId || entity.id == targetId)
            );
          }
          if (safeIndex >= 0) {
            const updatedEntry = { ...(panelEntries[safeIndex] ?? {}) };
            updatedEntry[fieldName] = value;
            const updatedEntries = [...panelEntries];
            updatedEntries[safeIndex] = updatedEntry;
            tempEntity[fieldParentName] = updatedEntries;
          }
        }
      } else if (fieldParentName) {
        if (!tempEntity[fieldParentName]) {
          tempEntity[fieldParentName] = {};
        }
        tempEntity[fieldParentName] = {
          ...tempEntity[fieldParentName],
          [fieldName]: value,
        };
      } else {
        tempEntity[fieldName] = value;
      }

      const errors = validate(
        tempEntity,
        [validation as IValidation],
        params.metadata.fields as IField[]
      );

      const newErrors = [
        ...(params.validationErrors?.filter((e) => e.fieldName !== fieldName) ||
          []),
        ...errors,
      ];

      params.onValidationErrorsChanged?.(newErrors);
    },
    [
      otherInfoObj,
      params,
      getRepeatingPanelIndex,
      currentElementInEntityRepeatingPanel,
    ]
  );

  const validateAllMandatoryFields = useCallback(() => {
    if (
      !params.metadata.validations ||
      params.metadata.validations.length === 0
    ) {
      return [];
    }

    const mandatoryFields = params.metadata.fields.filter((field) => {
      if (field.fieldType === 'FileViewer') return false;
      if (!isFieldCompletable(field, Boolean(params.isContractorRole))) {
        return false;
      }
      if (field.mandatory) return true;
      const validation = params.metadata.validations?.find(
        (v) => v.field === field.name
      );
      return validation?.rules?.some((rule) => rule.rule === 'NE');
    });

    const allErrors: IValidationError[] = [];
    const tempEntity = { ...otherInfoObj };

    mandatoryFields.forEach((field) => {
      const validation = params.metadata.validations?.find(
        (v) => v.field === field.name
      );
      if (!validation) return;

      const { fieldParentName, manualRepeating, entityRepeating } =
        getFieldPanelContext(field.name, params.metadata.fields);

      if (fieldParentName && (manualRepeating || entityRepeating)) {
        const panelEntries = tempEntity[fieldParentName];
        if (Array.isArray(panelEntries) && panelEntries.length > 0) {
          panelEntries.forEach((entry: any, _index: number) => {
            const entryEntity: any = {};
            Object.keys(tempEntity).forEach((key) => {
              if (key !== fieldParentName) {
                entryEntity[key] = tempEntity[key];
              }
            });
            entryEntity[fieldParentName] = [entry];

            const errors = validate(
              entryEntity,
              [validation as IValidation],
              params.metadata.fields as IField[]
            );
            allErrors.push(...errors);
          });
        } else {
          const errors = validate(
            tempEntity,
            [validation as IValidation],
            params.metadata.fields as IField[]
          );
          allErrors.push(...errors);
        }
      } else if (fieldParentName) {
        const errors = validate(
          tempEntity,
          [validation as IValidation],
          params.metadata.fields as IField[]
        );
        allErrors.push(...errors);
      } else {
        const errors = validate(
          tempEntity,
          [validation as IValidation],
          params.metadata.fields as IField[]
        );
        allErrors.push(...errors);
      }
    });

    if (allErrors.length > 0) {
      params.onValidationErrorsChanged?.(allErrors);
    } else {
      params.onValidationErrorsChanged?.([]);
    }

    return allErrors;
  }, [otherInfoObj, params]);

  return {
    otherInfo,
    otherInfoObj,
    completion,
    completionPercentage: completion.percentage,
    completedFieldsCount: completion.completedFields,
    totalCompletableFields: completion.totalFields,
    fieldCompletionIndicators,
    panelCompletionIndicators,
    getFieldCompletionIndicator,
    getPanelCompletionIndicator,
    isFieldComplete,
    isPanelComplete,
    allOptionsObj,
    getRepeatingPanelIndex,
    setRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    setCurrentElementInEntityRepeatingPanel,
    handleOtherInfoChange,
    handleOtherInfoBatchChange,
    handleOtherInfoChangeWithEntity,
    handleOtherInfoChangeWithManualIndex,
    handleRepeatingPanelAction,
    ensureEntityRepeatingEntry,
    validateField,
    validateAllMandatoryFields,
    hasActiveRepeatingInstance,
    hasActiveEntityRepeatingInstance,
    effectsKey,
  };
};
